import { FC, useState } from 'react';

import { Link, usePage } from '@inertiajs/react';
import { Heart } from 'lucide-react';

import { formatReleaseDate } from './gameReleaseDate';
import { GameReleaseFeature } from './GameReleaseFeature';
import { GameReleasesList } from './GameReleasesList';
import { useResolvedTheme } from '../../hooks/useResolvedTheme';
import { Routes } from '../../models/routes';
import { Expansion, ProductType, Universe } from '../../types';

type PageProps = {
    currentUniverse: Universe;
    currentProductType: ProductType | null;
    currentExpansion: Expansion | null;
    releaseHighlights: Expansion[];
};

const releaseHref = (
    universe: Universe,
    productTypeSlug: string,
    release: Expansion,
) => `/${universe.slug}/${productTypeSlug}/${release.slug}`;

export const GameReleasesHero: FC = () => {
    const {
        currentUniverse,
        currentProductType,
        currentExpansion,
        releaseHighlights,
    } = usePage<PageProps>().props;
    const [selectedReleaseId, setSelectedReleaseId] = useState<number | null>(
        releaseHighlights?.[0]?.id ?? null,
    );
    const resolvedTheme = useResolvedTheme();

    if (currentProductType || currentExpansion || !releaseHighlights?.length) {
        return null;
    }

    const selectedRelease =
        releaseHighlights.find((release) => release.id === selectedReleaseId) ??
        releaseHighlights[0];
    const singlesProductType =
        currentUniverse.product_types.find(
            (productType) => productType.slug === 'singles',
        ) ?? currentUniverse.product_types[0];

    if (!singlesProductType) return null;

    const universeHero =
        resolvedTheme === 'dark'
            ? (currentUniverse.hero_banner_dark ?? currentUniverse.hero_banner)
            : currentUniverse.hero_banner;
    const selectedImage = selectedRelease.image ?? universeHero;
    const selectedHref = releaseHref(
        currentUniverse,
        singlesProductType.slug,
        selectedRelease,
    );
    const releaseDate = formatReleaseDate(selectedRelease.released_on);

    return (
        <section
            className="py-4 sm:py-5 lg:py-6"
            data-testid="game-releases-hero"
        >
            <div className="container px-4 sm:px-6 lg:px-8">
                <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                            {currentUniverse.name}
                        </h1>
                    </div>

                    <nav
                        className="scrollbar-hide -mx-1 hidden max-w-full items-center gap-1 overflow-x-auto px-1 pb-1 lg:flex"
                        aria-label={`Навігація ${currentUniverse.name}`}
                    >
                        <Link
                            href={Routes.Wishlist}
                            className="inline-flex h-10 shrink-0 items-center gap-2 rounded-full px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                        >
                            <Heart className="h-4 w-4" />
                            Список бажань
                        </Link>
                    </nav>
                </div>

                <div className="grid gap-3 lg:h-[24rem] lg:grid-cols-[minmax(0,3fr)_minmax(20rem,1fr)]">
                    <GameReleaseFeature
                        release={selectedRelease}
                        image={selectedImage}
                        href={selectedHref}
                        releaseDate={releaseDate}
                    />

                    <GameReleasesList
                        releases={releaseHighlights}
                        selectedReleaseId={selectedRelease.id}
                        fallbackImage={universeHero}
                        onSelect={setSelectedReleaseId}
                    />
                </div>
            </div>
        </section>
    );
};
